import { LngLatBounds } from 'mapbox-gl';
import { BaseNode, HeatmapListItem } from '../hooks/useNearbyBaseNodes';
import {
  calculateAverageLocation,
  createAzimuthArcFromSite,
} from './nearbyBNsUtils';
import {
  EARTH_RADIUS_METERS,
  DEGREES_TO_RADIANS,
  DEFAULT_RADIUS_FROM_BOUNDS_METERS,
} from '../constants/geographicConstants';
import { MAX_BASE_NODES_PER_SITE } from '../constants/uiConstants';
import { ERROR_MESSAGES } from '../constants/errorConstants';

export interface Bounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export const getBNPropertyKey = (index: number, property: string): string => {
  return `bn${index}${property.charAt(0).toUpperCase() + property.slice(1)}`;
};

export const buildSiteFeatures = (
  bnsBySite: Record<string, BaseNode[]>
): GeoJSON.Feature[] => {
  const siteFeatures: GeoJSON.Feature[] = [];

  Object.entries(bnsBySite).forEach(([siteId, siteBNs]) => {
    const siteLocation = calculateAverageLocation(siteBNs);
    if (!siteLocation) return;

    const siteFeature: GeoJSON.Feature = {
      type: 'Feature',
      id: siteId,
      properties: {
        siteId,
        bnCount: siteBNs.length,
        ...siteBNs.slice(0, MAX_BASE_NODES_PER_SITE).reduce(
          (acc, bn, index) => {
            acc[getBNPropertyKey(index, 'serialNumber')] = bn.serial;
            acc[getBNPropertyKey(index, 'azimuth')] = bn.azimuth ?? null;
            acc[getBNPropertyKey(index, 'latitude')] = bn.latitude;
            acc[getBNPropertyKey(index, 'longitude')] = bn.longitude;
            acc[getBNPropertyKey(index, 'node')] = bn;
            return acc;
          },
          {} as Record<string, any>
        ),
      },
      geometry: {
        type: 'Point',
        coordinates: [siteLocation.longitude, siteLocation.latitude],
      },
    };

    siteFeatures.push(siteFeature);
  });

  return siteFeatures;
};

export const buildArcFeatures = (
  bnsBySite: Record<string, BaseNode[]>
): GeoJSON.Feature[] => {
  const arcFeatures: GeoJSON.Feature[] = [];

  Object.entries(bnsBySite).forEach(([siteId, siteBNs]) => {
    const siteLocation = calculateAverageLocation(siteBNs);
    if (!siteLocation) return;

    siteBNs.slice(0, MAX_BASE_NODES_PER_SITE).forEach((bn, index) => {
      if (bn.azimuth !== null && bn.azimuth !== undefined) {
        const arcCoordinates = createAzimuthArcFromSite(
          siteLocation,
          bn.azimuth
        );
        if (arcCoordinates) {
          const arcId = `arc-${siteId}-${index}`;
          const arcFeature: GeoJSON.Feature = {
            type: 'Feature',
            id: arcId,
            properties: {
              arcId,
              siteId,
              bnIndex: index,
              serial: bn.serial,
              azimuth: bn.azimuth,
            },
            geometry: {
              type: 'Polygon',
              coordinates: [arcCoordinates],
            },
          };
          arcFeatures.push(arcFeature);
        }
      }
    });
  });

  return arcFeatures;
};

/**
 * Convert mapbox-gl LngLatBounds to a plain object
 */
export const boundsToPlainObject = (
  bounds: LngLatBounds | null
): Bounds | null => {
  if (!bounds) {
    return null;
  }
  return {
    north: bounds.getNorth(),
    south: bounds.getSouth(),
    east: bounds.getEast(),
    west: bounds.getWest(),
  };
};

/**
 * Calculate the radius in meters from the map bounds
 * Uses the Haversine formula to calculate distances from center to corners
 * Returns the maximum distance (radius) to cover the entire visible area
 */
export const calculateRadiusFromBounds = (bounds: Bounds | null): number => {
  if (!bounds) {
    return DEFAULT_RADIUS_FROM_BOUNDS_METERS;
  }

  const toRadians = (degrees: number): number => degrees * DEGREES_TO_RADIANS;

  const { north, east, south, west } = bounds;

  const centerLat = (north + south) / 2;
  const centerLng = (east + west) / 2;

  // Haversine formula
  const haversineDistance = (
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number => {
    const dLat = toRadians(lat2 - lat1);
    const dLng = toRadians(lng2 - lng1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRadians(lat1)) *
        Math.cos(toRadians(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return EARTH_RADIUS_METERS * c;
  };

  const distances = [
    haversineDistance(centerLat, centerLng, north, east),
    haversineDistance(centerLat, centerLng, north, west),
    haversineDistance(centerLat, centerLng, south, east),
    haversineDistance(centerLat, centerLng, south, west),
  ];

  const radius = Math.max(...distances);
  return radius;
};

/**
 * Calculate distance from center to edge of bounds (in meters)
 * Similar to mobile implementation: distance from center to north edge
 */
const getDistanceToEdge = (centerLat: number, bounds: Bounds): number => {
  const toRadians = (degrees: number): number => degrees * DEGREES_TO_RADIANS;

  const { north } = bounds;

  // Calculate distance from center to north edge at same longitude
  const dLat = toRadians(north - centerLat);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_METERS * c;
};

const data = {
  baseNodeList: {
    operator: 'Sail Internet',
    latitude: 37.421157,
    longitude: -121.887833,
    radius: 13160,
    bn_list: [
      {
        operator: 'Sail Internet',
        region: 'Sail Internet',
        market: 'SNTC',
        site: '307_Market_St',
        cell: '307MarketSt',
        sector: 'tbn-307marketst-nw',
        hostname: 'tbn-307marketst-nw',
        serial: 'S134F2215000084',
        latitude: 37.347071,
        longitude: -121.940428,
        height_agl: 38,
        azimuth: 285,
        downtilt: -1,
        carrier: [
          {
            frequency: 5765,
            bandwidth: 40,
            tx_power: 18,
          },
          {
            frequency: 5210,
            bandwidth: 40,
            tx_power: 18,
          },
        ],
        network_profile: 1,
        height_gps: 56.9,
        ground_msl: 24.7,
        sector_cnt: 2,
        subscribers: 36,
        planning_id: '1.3.0',
        part_number: '30-0134-001',
        firmware: '3.207',
        timestamp: '2025-11-13',
        data_issues: '',
        heatmap_legends: {
          base_url:
            'https://storage.googleapis.com/tw-network-planning-dev/heatmaps/legend/',
          confidence: 'confidence_all.png',
          fresnel_clearance: 'fresnel_clearance.png',
          path_loss_3ghz: 'path_loss_3ghz.png',
          path_loss_5ghz: 'path_loss_5ghz.png',
          linkspeed_x2: 'linkspeed_x2.png',
          linkspeed_3ghz: 'linkspeed_3ghz.png',
          linkspeed_5ghz: 'linkspeed_5ghz.png',
        },
        heatmap_list: [
          {
            height: {
              above_open: 5,
              above_roof: 1,
            },
            base_url:
              'https://storage.googleapis.com/tw-network-planning-dev/heatmaps/Sail_Internet/696887859432/S134F2215000084/Heights__5_0_1_0/',
            geotiff: {
              gnp_output: {
                free_space: 'free_space/path_loss.tif',
                fixed_wireless: 'fixed_wireless/path_loss.tif',
                mobility_macro: 'mobility_macro/path_loss.tif',
                mobility_micro: 'mobility_micro/path_loss.tif',
                fresnel_clearance: 'fixed_wireless/fresnel_clearance.tif',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/path_loss.tif',
                link_speed: 'fixed_wireless/path_loss.tif',
                confidence: 'aiml_prediction/confidence.tif',
              },
            },
            kmz: {
              gnp_output: {
                free_space: 'free_space/path_loss.kmz',
                fixed_wireless: 'fixed_wireless/path_loss.kmz',
                mobility_macro: 'mobility_macro/path_loss.kmz',
                mobility_micro: 'mobility_micro/path_loss.kmz',
                fresnel_clearance: 'fixed_wireless/fresnel_clearance.kmz',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/path_loss.kmz',
                link_speed: 'fixed_wireless/path_loss.kmz',
                confidence: 'aiml_prediction/confidence.kmz',
              },
            },
            kml: {
              gnp_output: {
                free_space: 'free_space/path_loss_doc.kml',
                fixed_wireless: 'fixed_wireless/path_loss_doc.kml',
                mobility_macro: 'mobility_macro/path_loss_doc.kml',
                mobility_micro: 'mobility_micro/path_loss_doc.kml',
                fresnel_clearance: 'fixed_wireless/fresnel_clearance_doc.kml',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/path_loss_doc.kml',
                link_speed: 'fixed_wireless/path_loss_doc.kml',
                confidence: 'aiml_prediction/confidence_doc.kml',
              },
            },
            overlay: {
              gnp_output: {
                free_space: 'free_space/path_loss_overlay.png',
                fixed_wireless: 'fixed_wireless/path_loss_overlay.png',
                mobility_macro: 'mobility_macro/path_loss_overlay.png',
                mobility_micro: 'mobility_micro/path_loss_overlay.png',
                fresnel_clearance:
                  'fixed_wireless/fresnel_clearance_overlay.png',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/path_loss_overlay.png',
                link_speed: 'fixed_wireless/path_loss_overlay.png',
                confidence: 'aiml_prediction/confidence_overlay.png',
              },
            },
            legend: {
              gnp_output: {
                free_space: 'free_space/path_loss_legend.png',
                fixed_wireless: 'fixed_wireless/path_loss_legend.png',
                mobility_macro: 'mobility_macro/path_loss_legend.png',
                mobility_micro: 'mobility_micro/path_loss_legend.png',
                fresnel_clearance:
                  'fixed_wireless/fresnel_clearance_legend.png',
              },
              aiml_output: {
                path_loss:
                  'https://storage.googleapis.com/tw-network-planning-dev/heatmaps/legend/path_loss_5ghz.png',
                link_speed:
                  'https://storage.googleapis.com/tw-network-planning-dev/heatmaps/legend/linkspeed_5ghz.png',
                confidence:
                  'https://storage.googleapis.com/tw-network-planning-dev/heatmaps/legend/confidence_all.png',
              },
            },
            tif_tiles: {
              gnp_output: {
                free_space: 'free_space/tiles_13/tif/',
                fixed_wireless: 'fixed_wireless/tiles_13/tif/',
                mobility_macro: 'mobility_macro/tiles_13/tif/',
                mobility_micro: 'mobility_micro/tiles_13/tif/',
                fresnel_clearance: 'fresnel_clearance/tiles_13/tif/',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/tiles_13/tif/',
                link_speed: 'fixed_wireless/tiles_13/tif/',
                confidence: 'confidence/tiles_13/tif/',
              },
            },
            kmz_tiles: {
              gnp_output: {
                free_space: 'free_space/tiles_13/kmz/',
                fixed_wireless: 'fixed_wireless/tiles_13/kmz/',
                mobility_macro: 'mobility_macro/tiles_13/kmz/',
                mobility_micro: 'mobility_micro/tiles_13/kmz/',
                fresnel_clearance: 'fresnel_clearance/tiles_13/kmz/',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/tiles_13/kmz/',
                link_speed: 'fixed_wireless/tiles_13/kmz/',
                confidence: 'confidence/tiles_13/kmz/',
              },
            },
          },
          {
            height: {
              above_open: 7,
              above_roof: 2,
            },
            base_url:
              'https://storage.googleapis.com/tw-network-planning-dev/heatmaps/Sail_Internet/696887859432/S134F2215000084/Heights__7_0_2_0/',
            geotiff: {
              gnp_output: {
                free_space: 'free_space/path_loss.tif',
                fixed_wireless: 'fixed_wireless/path_loss.tif',
                mobility_macro: 'mobility_macro/path_loss.tif',
                mobility_micro: 'mobility_micro/path_loss.tif',
                fresnel_clearance: 'fixed_wireless/fresnel_clearance.tif',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/path_loss.tif',
                link_speed: 'fixed_wireless/path_loss.tif',
                confidence: 'aiml_prediction/confidence.tif',
              },
            },
            kmz: {
              gnp_output: {
                free_space: 'free_space/path_loss.kmz',
                fixed_wireless: 'fixed_wireless/path_loss.kmz',
                mobility_macro: 'mobility_macro/path_loss.kmz',
                mobility_micro: 'mobility_micro/path_loss.kmz',
                fresnel_clearance: 'fixed_wireless/fresnel_clearance.kmz',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/path_loss.kmz',
                link_speed: 'fixed_wireless/path_loss.kmz',
                confidence: 'aiml_prediction/confidence.kmz',
              },
            },
            kml: {
              gnp_output: {
                free_space: 'free_space/path_loss_doc.kml',
                fixed_wireless: 'fixed_wireless/path_loss_doc.kml',
                mobility_macro: 'mobility_macro/path_loss_doc.kml',
                mobility_micro: 'mobility_micro/path_loss_doc.kml',
                fresnel_clearance: 'fixed_wireless/fresnel_clearance_doc.kml',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/path_loss_doc.kml',
                link_speed: 'fixed_wireless/path_loss_doc.kml',
                confidence: 'aiml_prediction/confidence_doc.kml',
              },
            },
            overlay: {
              gnp_output: {
                free_space: 'free_space/path_loss_overlay.png',
                fixed_wireless: 'fixed_wireless/path_loss_overlay.png',
                mobility_macro: 'mobility_macro/path_loss_overlay.png',
                mobility_micro: 'mobility_micro/path_loss_overlay.png',
                fresnel_clearance:
                  'fixed_wireless/fresnel_clearance_overlay.png',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/path_loss_overlay.png',
                link_speed: 'fixed_wireless/path_loss_overlay.png',
                confidence: 'aiml_prediction/confidence_overlay.png',
              },
            },
            legend: {
              gnp_output: {
                free_space: 'free_space/path_loss_legend.png',
                fixed_wireless: 'fixed_wireless/path_loss_legend.png',
                mobility_macro: 'mobility_macro/path_loss_legend.png',
                mobility_micro: 'mobility_micro/path_loss_legend.png',
                fresnel_clearance:
                  'fixed_wireless/fresnel_clearance_legend.png',
              },
              aiml_output: {
                path_loss:
                  'https://storage.googleapis.com/tw-network-planning-dev/heatmaps/legend/path_loss_5ghz.png',
                link_speed:
                  'https://storage.googleapis.com/tw-network-planning-dev/heatmaps/legend/linkspeed_5ghz.png',
                confidence:
                  'https://storage.googleapis.com/tw-network-planning-dev/heatmaps/legend/confidence_all.png',
              },
            },
            tif_tiles: {
              gnp_output: {
                free_space: 'free_space/tiles_13/tif/',
                fixed_wireless: 'fixed_wireless/tiles_13/tif/',
                mobility_macro: 'mobility_macro/tiles_13/tif/',
                mobility_micro: 'mobility_micro/tiles_13/tif/',
                fresnel_clearance: 'fresnel_clearance/tiles_13/tif/',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/tiles_13/tif/',
                link_speed: 'fixed_wireless/tiles_13/tif/',
                confidence: 'confidence/tiles_13/tif/',
              },
            },
            kmz_tiles: {
              gnp_output: {
                free_space: 'free_space/tiles_13/kmz/',
                fixed_wireless: 'fixed_wireless/tiles_13/kmz/',
                mobility_macro: 'mobility_macro/tiles_13/kmz/',
                mobility_micro: 'mobility_micro/tiles_13/kmz/',
                fresnel_clearance: 'fresnel_clearance/tiles_13/kmz/',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/tiles_13/kmz/',
                link_speed: 'fixed_wireless/tiles_13/kmz/',
                confidence: 'confidence/tiles_13/kmz/',
              },
            },
          },
          {
            height: {
              above_open: 10,
              above_roof: 3,
            },
            base_url:
              'https://storage.googleapis.com/tw-network-planning-dev/heatmaps/Sail_Internet/696887859432/S134F2215000084/Heights_10_0_3_0/',
            geotiff: {
              gnp_output: {
                free_space: 'free_space/path_loss.tif',
                fixed_wireless: 'fixed_wireless/path_loss.tif',
                mobility_macro: 'mobility_macro/path_loss.tif',
                mobility_micro: 'mobility_micro/path_loss.tif',
                fresnel_clearance: 'fixed_wireless/fresnel_clearance.tif',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/path_loss.tif',
                link_speed: 'fixed_wireless/path_loss.tif',
                confidence: 'aiml_prediction/confidence.tif',
              },
            },
            kmz: {
              gnp_output: {
                free_space: 'free_space/path_loss.kmz',
                fixed_wireless: 'fixed_wireless/path_loss.kmz',
                mobility_macro: 'mobility_macro/path_loss.kmz',
                mobility_micro: 'mobility_micro/path_loss.kmz',
                fresnel_clearance: 'fixed_wireless/fresnel_clearance.kmz',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/path_loss.kmz',
                link_speed: 'fixed_wireless/path_loss.kmz',
                confidence: 'aiml_prediction/confidence.kmz',
              },
            },
            kml: {
              gnp_output: {
                free_space: 'free_space/path_loss_doc.kml',
                fixed_wireless: 'fixed_wireless/path_loss_doc.kml',
                mobility_macro: 'mobility_macro/path_loss_doc.kml',
                mobility_micro: 'mobility_micro/path_loss_doc.kml',
                fresnel_clearance: 'fixed_wireless/fresnel_clearance_doc.kml',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/path_loss_doc.kml',
                link_speed: 'fixed_wireless/path_loss_doc.kml',
                confidence: 'aiml_prediction/confidence_doc.kml',
              },
            },
            overlay: {
              gnp_output: {
                free_space: 'free_space/path_loss_overlay.png',
                fixed_wireless: 'fixed_wireless/path_loss_overlay.png',
                mobility_macro: 'mobility_macro/path_loss_overlay.png',
                mobility_micro: 'mobility_micro/path_loss_overlay.png',
                fresnel_clearance:
                  'fixed_wireless/fresnel_clearance_overlay.png',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/path_loss_overlay.png',
                link_speed: 'fixed_wireless/path_loss_overlay.png',
                confidence: 'aiml_prediction/confidence_overlay.png',
              },
            },
            legend: {
              gnp_output: {
                free_space: 'free_space/path_loss_legend.png',
                fixed_wireless: 'fixed_wireless/path_loss_legend.png',
                mobility_macro: 'mobility_macro/path_loss_legend.png',
                mobility_micro: 'mobility_micro/path_loss_legend.png',
                fresnel_clearance:
                  'fixed_wireless/fresnel_clearance_legend.png',
              },
              aiml_output: {
                path_loss:
                  'https://storage.googleapis.com/tw-network-planning-dev/heatmaps/legend/path_loss_5ghz.png',
                link_speed:
                  'https://storage.googleapis.com/tw-network-planning-dev/heatmaps/legend/linkspeed_5ghz.png',
                confidence:
                  'https://storage.googleapis.com/tw-network-planning-dev/heatmaps/legend/confidence_all.png',
              },
            },
            tif_tiles: {
              gnp_output: {
                free_space: 'free_space/tiles_13/tif/',
                fixed_wireless: 'fixed_wireless/tiles_13/tif/',
                mobility_macro: 'mobility_macro/tiles_13/tif/',
                mobility_micro: 'mobility_micro/tiles_13/tif/',
                fresnel_clearance: 'fresnel_clearance/tiles_13/tif/',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/tiles_13/tif/',
                link_speed: 'fixed_wireless/tiles_13/tif/',
                confidence: 'confidence/tiles_13/tif/',
              },
            },
            kmz_tiles: {
              gnp_output: {
                free_space: 'free_space/tiles_13/kmz/',
                fixed_wireless: 'fixed_wireless/tiles_13/kmz/',
                mobility_macro: 'mobility_macro/tiles_13/kmz/',
                mobility_micro: 'mobility_micro/tiles_13/kmz/',
                fresnel_clearance: 'fresnel_clearance/tiles_13/kmz/',
              },
              aiml_output: {
                path_loss: 'fixed_wireless/tiles_13/kmz/',
                link_speed: 'fixed_wireless/tiles_13/kmz/',
                confidence: 'confidence/tiles_13/kmz/',
              },
            },
          },
        ],
      },
    ],
  },
};

/**
 * Check if a center change is significant
 * Uses half the distance from center to edge of screen as threshold
 * @param oldCenter - Previous center coordinates [lng, lat]
 * @param newCenter - New center coordinates [lng, lat]
 * @param bounds - Map bounds (plain object from last findBNs call)
//  * @returns true if the change is significant, false otherwise
 */
export const isSignificantCenterChange = (
  oldCenter: [number, number] | null,
  newCenter: [number, number],
  bounds: Bounds | null
): boolean => {
  if (!oldCenter || !bounds) {
    return true;
  }

  const toRadians = (degrees: number): number => degrees * DEGREES_TO_RADIANS;

  const [oldLng, oldLat] = oldCenter;
  const [newLng, newLat] = newCenter;

  // Calculate distance between old and new center
  const dLat = toRadians(newLat - oldLat);
  const dLng = toRadians(newLng - oldLng);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(oldLat)) *
      Math.cos(toRadians(newLat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = EARTH_RADIUS_METERS * c;
  const distanceKm = distance / 1000;

  const [centerLng, centerLat] = newCenter;
  const distanceToEdge = getDistanceToEdge(centerLat, bounds);
  const distanceToEdgeKm = distanceToEdge / 1000;

  const threshold = distanceToEdge / 2;
  const thresholdKm = threshold / 1000;

  const isSignificant = distance > threshold;

  return isSignificant;
};

/**
 * Heatmap bounds interface
 */
export interface HeatmapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Heatmap URLs interface
 */
export interface HeatmapUrls {
  kmlUrl: string;
  overlayUrl: string;
  legendUrl: string;
}

/**
 * Filter heatmap list by height
 * Height format: "above_roof_above_open" (e.g., "3_10")
 * @param heatmapList - Array of heatmap list items
 * @param height - Height string in format "above_roof_above_open"
 * @returns Filtered heatmap list item or null
 */
export const getHeatmapDataForHeight = (
  heatmapList: HeatmapListItem[] | undefined,
  height: string | null
): HeatmapListItem | null => {
  if (!heatmapList || !height) {
    return null;
  }

  const heightParts = height.split('_').map(Number);
  if (
    heightParts.length !== 2 ||
    isNaN(heightParts[0]) ||
    isNaN(heightParts[1])
  ) {
    console.warn(
      `${ERROR_MESSAGES.INVALID_HEIGHT_FORMAT} ${height}. ${ERROR_MESSAGES.EXPECTED_HEIGHT_FORMAT}`
    );
    return null;
  }

  const [aboveRoof, aboveOpen] = heightParts;

  const filtered = heatmapList.find(
    (item) =>
      item.height.above_roof === aboveRoof &&
      item.height.above_open === aboveOpen
  );

  return filtered || null;
};

/**
 * Extract bounds from KML XML document
 * @param kmlDoc - Parsed KML XML document
 * @returns Bounds object or null if not found
 */
export const extractBoundsFromKML = (
  kmlDoc: Document
): HeatmapBounds | null => {
  const latLonBox = kmlDoc.getElementsByTagName('LatLonBox')[0];

  if (!latLonBox) {
    return null;
  }

  const getValue = (tagName: string): number | null => {
    const element = latLonBox.getElementsByTagName(tagName)[0];
    return element ? parseFloat(element.textContent || '') : null;
  };

  const north = getValue('north');
  const south = getValue('south');
  const east = getValue('east');
  const west = getValue('west');

  if (north === null || south === null || east === null || west === null) {
    return null;
  }

  return { north, south, east, west };
};

/**
 * Get heatmap URLs for a specific heatmap type and height
 * @param heatmapItem - Filtered heatmap list item
 * @param heatmapType - Type of heatmap (e.g., 'confidence', 'path_loss', 'link_speed')
 * @returns Object with KML, overlay, and legend URLs
 */
export const getHeatmapUrls = (
  heatmapItem: HeatmapListItem | null,
  heatmapType: string | null
): HeatmapUrls | null => {
  if (!heatmapItem || !heatmapType) {
    return null;
  }

  const { base_url, kml, overlay, legend } = heatmapItem;

  const kmlPath =
    kml?.aiml_output?.[heatmapType as keyof typeof kml.aiml_output];
  const overlayPath =
    overlay?.aiml_output?.[heatmapType as keyof typeof overlay.aiml_output];
  const legendPath =
    legend?.aiml_output?.[heatmapType as keyof typeof legend.aiml_output];

  if (!kmlPath || !overlayPath) {
    console.warn(`${ERROR_MESSAGES.HEATMAP_TYPE_MISSING} ${heatmapType}`);
    return null;
  }

  return {
    kmlUrl: base_url + kmlPath,
    overlayUrl: base_url + overlayPath,
    legendUrl: legendPath
      ? legendPath.startsWith('http')
        ? legendPath
        : base_url + legendPath
      : '',
  };
};
